/**
 * Recherche d'un sous-poste à la saisie : insensible à la casse et aux accents,
 * tous les mots saisis doivent se retrouver dans « code + libellé + poste ».
 * Fonctions pures, sans accès au DOM.
 */

import { normaliser } from '@scc/core';
import { comparerSousPostes, grouperSousPostesParPoste, type SousPosteAffichable } from './comptes.js';

/** Nombre maximal de résultats affichés dans la liste déroulante. */
export const LIMITE_RESULTATS = 50;

/**
 * Sous-postes dont le code, le libellé ou le libellé du poste contiennent chacun
 * des mots de `saisie` (« banc » → « Frais bancaires et charges », « 6262 » →
 * « C6262 - Frais de téléphonie »). Saisie vide : tous. L'ordre est celui des
 * listes déroulantes : par poste, le générique en tête puis les comptes par code.
 */
export function filtrerSousPostes<T extends SousPosteAffichable>(
  choix: readonly T[],
  saisie: string,
  libellePoste: (poste: string) => string,
): T[] {
  const mots = normaliser(saisie)
    .split(' ')
    .filter((m) => m !== '');
  const retenus =
    mots.length === 0
      ? choix
      : choix.filter((s) => {
          const texte = normaliser(`${s.code} ${s.libelle} ${libellePoste(s.poste)}`);
          return mots.every((m) => texte.includes(m));
        });
  return grouperSousPostesParPoste(retenus).flatMap(([, membres]) => [...membres].sort(comparerSousPostes));
}

export interface ResultatsGroupes<T> {
  /** Les `max` premiers résultats, regroupés par poste (déjà contigus dans l'ordre d'entrée). */
  readonly groupes: readonly (readonly [string, readonly T[]])[];
  /** Résultats non affichés. */
  readonly restants: number;
}

/** Garde les `max` premiers résultats et les regroupe par poste en conservant l'ordre. */
export function limiterEtGrouper<T extends SousPosteAffichable>(
  resultats: readonly T[],
  max: number = LIMITE_RESULTATS,
): ResultatsGroupes<T> {
  const groupes: [string, T[]][] = [];
  for (const s of resultats.slice(0, max)) {
    const dernier = groupes[groupes.length - 1];
    if (dernier?.[0] === s.poste) dernier[1].push(s);
    else groupes.push([s.poste, [s]]);
  }
  return { groupes, restants: Math.max(0, resultats.length - max) };
}
